import type { Subscription, SubscriptionPlan } from '@thinkalpha/access-ws-client/contracts/subscription.js';
import type { ProTraderStatus } from '@thinkalpha/common/schemas/auth.js';
import type { z } from 'zod';

type Plan = z.infer<typeof SubscriptionPlan>;
type SubscriptionType = z.infer<typeof Subscription>;

const PRO_TRADER_STATUS_TO_IS_PROFESSIONAL_TRADER = {
    pro: true,
    'non-pro': false,
    unspecified: false,
} satisfies Record<ProTraderStatus, boolean>;

export const ACTIVE_SUBSCRIPTION_STATUSES: ReadonlySet<SubscriptionType['status']> = new Set([
    'active',
    'trialing',
    'active_canceling',
    'active_plan_change_scheduled',
    'pending_activation',
]);

export const PLAN_CHANGEABLE_SUBSCRIPTION_STATUSES: ReadonlySet<SubscriptionType['status']> = new Set([
    'active',
    'trialing',
    'active_plan_change_scheduled',
]);

export function statusToIsProfessionalTrader(status: ProTraderStatus): boolean {
    return PRO_TRADER_STATUS_TO_IS_PROFESSIONAL_TRADER[status];
}

export function isPlanAvailableForTraderStatus(plan: Plan, isProfessionalTrader: boolean): boolean {
    return isProfessionalTrader ? plan.professionalTraderPrice !== false : plan.price !== false;
}

export function isPlanSelectableForTraderStatus(plan: Plan, isProfessionalTrader: boolean): boolean {
    return plan.active === true && isPlanAvailableForTraderStatus(plan, isProfessionalTrader);
}

export function isActiveSubscriptionStatus(status: SubscriptionType['status']): boolean {
    return ACTIVE_SUBSCRIPTION_STATUSES.has(status);
}

export function isPlanChangeableSubscriptionStatus(status: SubscriptionType['status']): boolean {
    return PLAN_CHANGEABLE_SUBSCRIPTION_STATUSES.has(status);
}

export function isMainSubscription(subscription: Pick<SubscriptionType, 'plan'>): boolean {
    return subscription.plan.isAddOn !== true;
}

export function getPlanChangeSubscription(
    subscriptions: ReadonlyArray<SubscriptionType>,
): SubscriptionType | undefined {
    return subscriptions.find((subscription) => {
        return isMainSubscription(subscription) && isPlanChangeableSubscriptionStatus(subscription.status);
    });
}
